/**
 * Video Render
 *
 * Rendering and thumbnail helpers for the video picker UI.
 */

import { VideoCanvas } from "./video-canvas";
import { VideoListener } from "./video-listener";

interface VideoThumbnailData {
    id: number;
    file: File;
    name: string;
    size: number;
    duration: number;
    previewUrl: string;
    videoUrl: string;
    url: string;
}

type ResolveCallback = (value: VideoThumbnailData) => void;
type RejectCallback = (reason?: unknown) => void;

class VideoRender {
    public static async generateThumbnail(file: File): Promise<VideoThumbnailData> {
        return new Promise((resolve: ResolveCallback, reject: RejectCallback) => {
            if (!file.type.startsWith("video/")) {
                reject(new Error("File is not a video"));
                return;
            }

            const video = document.createElement("video");
            const url = URL.createObjectURL(file);

            video.muted = true;
            video.playsInline = true;
            video.preload = "metadata";
            // Position off-screen at a real size so Android Chrome does not skip decoding.
            // opacity:0 / width:1px triggers a browser optimization that skips video decode.
            video.style.cssText =
                "position:fixed;left:-200px;top:-200px;width:160px;height:90px;pointer-events:none;";

            document.body.appendChild(video);

            let settled = false;
            let pollId: ReturnType<typeof setInterval> | null = null;

            const stopPoll = (): void => {
                if (pollId !== null) {
                    clearInterval(pollId);
                    pollId = null;
                }
            };

            const cleanupOnError = (): void => {
                stopPoll();
                video.pause();
                URL.revokeObjectURL(url);
                if (video.parentNode) video.parentNode.removeChild(video);
            };

            const cleanupOnResolve = (): void => {
                stopPoll();
                video.pause();
                if (video.parentNode) video.parentNode.removeChild(video);
                // url kept alive — returned inside VideoThumbnailData.
            };

            const timeoutId = setTimeout(() => {
                if (settled) return;
                settled = true;
                cleanupOnError();
                reject(new Error("timeout"));
            }, 15000);

            const tryCapture = (): void => {
                if (settled) return;

                try {
                    const thumbnailDataUrl = VideoCanvas.drawVideoFrame(video);
                    if (!thumbnailDataUrl || thumbnailDataUrl === "data:,") return;

                    settled = true;
                    clearTimeout(timeoutId);
                    cleanupOnResolve();
                    resolve({
                        id: Date.now() + Math.random(),
                        file,
                        name: file.name,
                        size: file.size,
                        duration: isFinite(video.duration) && video.duration > 0 ? video.duration : 0,
                        previewUrl: thumbnailDataUrl,
                        videoUrl: url,
                        url,
                    });
                } catch (_e) {
                    // Frame not ready yet.
                }
            };

            const onMetadata = (): void => {
                const seekTime =
                    isFinite(video.duration) && video.duration > 0 ? Math.min(1, video.duration * 0.1) : 0;
                video.currentTime = seekTime;

                // requestVideoFrameCallback fires when an actual decoded frame is ready.
                // More reliable than seeked/loadeddata on Android Chrome.
                if (typeof video.requestVideoFrameCallback === "function") {
                    video.requestVideoFrameCallback(() => tryCapture());
                }
            };

            const onError = (): void => {
                if (settled) return;
                settled = true;
                clearTimeout(timeoutId);
                cleanupOnError();
                reject(new Error("video load error"));
            };

            VideoListener.setupThumbnailGenListeners(video, onMetadata, tryCapture, onError);

            video.src = url;
            video.load();
            video.play().catch(() => {});

            // Polling fallback for browsers where no events fire (older Android).
            pollId = setInterval(() => {
                if (settled) {
                    stopPoll();
                    return;
                }
                if (video.readyState >= 2) {
                    tryCapture();
                }
            }, 200);
        });
    }
}

export { VideoRender };
export type { VideoThumbnailData };
